'use client';

import type { SupportedLocaleType } from '@repo/config';
import { useState } from 'react';
import { useEnglishIqAnswer } from './use-english-iq-answer';
import { useEnglishIqFinish } from './use-english-iq-finish';
import { useEnglishIqStart } from './use-english-iq-start';
import { useEnglishIqStoredFlow } from './wizard/use-english-iq-stored-flow';
import { useResumeEnglishIqCompletion } from './wizard/use-resume-english-iq-completion';

export function useEnglishIqWizard(locale: SupportedLocaleType) {
  const stored = useEnglishIqStoredFlow(locale);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const complete = useEnglishIqFinish({
    clearSession: stored.clearSession,
    setError,
    setIsBusy,
    storeResult: stored.storeResult,
  });
  const start = useEnglishIqStart({
    locale,
    setError,
    setIsBusy,
    storeSession: stored.storeSession,
  });
  const attemptCompletion = useResumeEnglishIqCompletion(stored.completingToken, complete);
  const answer = useEnglishIqAnswer({
    clearSession: stored.clearSession,
    finish: attemptCompletion,
    session: stored.session,
    setError,
    setIsBusy,
    storeCompleting: stored.storeCompleting,
    storeSession: stored.storeSession,
  });

  const finish = async (token?: string): Promise<void> => {
    if (!token) {
      return;
    }
    stored.storeCompleting(token);
    await attemptCompletion(token);
  };

  const reset = (): void => {
    setError(null);
    stored.clearSession();
  };

  return {
    answer,
    clearMilestone: stored.clearMilestone,
    completingToken: stored.completingToken,
    error,
    finish,
    isBusy,
    isHydrated: stored.isHydrated,
    markStartFailed: () => setError('start'),
    milestone: stored.milestone,
    reset,
    result: stored.result,
    session: stored.session,
    start,
  };
}
